import { store, increaseShiftQueueNumber } from "@redux"
import calculateTotalDeliveryFee from "./calculateTotalDeliveryFee"
import calculateTotalFoodDiscounts from "./calculateTotalFoodDiscounts"
import calculateTaxesAndCharges from "./calculateTaxesAndCharges"
import onlyPositiveNumber from "./onlyPositiveNumber"
import calculateOrderItemsSubtotal from "./calculateOrderItemsSubtotal"
import createPrintRequest from "./createPrintRequest"
import moment from "./moment"

const getQueueNumber = (tempOrderId) => {
  const {
    shiftCashier,
    userAccountInfo: { _id: cashierAccountId }
  } = store.getState()

  const queueNumber =
    shiftCashier[cashierAccountId].queueNumbers[tempOrderId]

  if (!!queueNumber) return queueNumber

  const previousQueueNumber =
    Object.keys(shiftCashier[cashierAccountId].queueNumbers).length

  const newQueueNumber =
    previousQueueNumber + 1

  store.dispatch(
    increaseShiftQueueNumber(tempOrderId)
  )

  return newQueueNumber
}

const getPrintDetails = (
  tempOrderId,
  order,
  deliveryType,
  deliveryService,
  deliveryAddress,
  discounts,
  deliveryFees,
  orderStatus,
  selectedUserAccount
) => {
  const {
    systemSettings: {
      subdomain,
      domain,
      kitchenTitle,
      serviceTypeName
    }
  } = store.getState()

  const {
    totalDeliveryFee,
    totalDeliveryFeeBeforeDiscount,
    totalDeliveryDiscountByCoupon
  } =
    calculateTotalDeliveryFee(
      discounts,
      deliveryFees,
      deliveryType
    )

  const totalComboDiscounts =
    order.combos.reduce((t, combo) =>
      t += combo.comboDiscount
      , 0
    )

  const orderItemsSubtotal =
    calculateOrderItemsSubtotal(order.items)

  const subTotalComboItems =
    order.combos.reduce((t, combo) =>
      t +=
      combo.items.reduce((t, item) =>
        t +=
        (item.salePrice + item.addAmount) *
        item.quantity
        , 0
      )
      , 0
    )

  const totalSystemDiscounts =
    order.items.reduce((to, item) =>
      to += Math.max(item.systemDiscount, 0) * item.quantity
      , 0
    )

  const subtotal =
    orderItemsSubtotal +
    subTotalComboItems

  const totalFoodDiscounts =
    totalComboDiscounts +
    totalSystemDiscounts +
    calculateTotalFoodDiscounts(discounts)

  const totalDiscount =
    totalFoodDiscounts +
    totalDeliveryDiscountByCoupon

  const totalBeforeDiscount =
    subtotal +
    totalFoodDiscounts -
    totalSystemDiscounts +
    totalDeliveryFeeBeforeDiscount

  const taxesAndCharges =
    calculateTaxesAndCharges(
      order.combos,
      order.items,
      deliveryFees,
      discounts,
      deliveryType
    )

  const totalTax =
    taxesAndCharges.reduce((t, tax) =>
      t += !tax.isNotTax ? tax.amount : 0,
      0
    )

  const totalCharges =
    taxesAndCharges.reduce((t, charge) =>
      t += charge.isNotTax ? charge.amount : 0
      , 0
    )

  const total =
    onlyPositiveNumber(
      subtotal +
      totalTax +
      totalCharges +
      totalDeliveryFee -
      totalDiscount
    )

  const comboItems = []
  order.combos.map(combo =>
    combo.items.forEach(comboItem =>
      comboItems.push(comboItem)
    )
  )

  const items =
    [...order.items, ...comboItems].map(item => ({
      foodName: item.foodName,
      unit: item.unit,
      isSimple: item.isSimple,
      systemDiscount: item.systemDiscount,
      quantity: item.quantity,
      salePrice: item.salePrice,
      optionName: item.optionName,
      categoryIds: item.categoryIds,
      specificationItems: item.specificationItems || []
    }))

  const queueNumber =
    getQueueNumber(tempOrderId)

  return {
    _id: tempOrderId,
    code: "",
    note: order.note,
    date: moment().toISOString(),
    items,
    total,
    isCod: true,
    subtotal,
    orderStatus,
    subdomain,
    domain,
    paymentType: "cash",
    kitchenTitle,
    serviceTypeName,
    totalBeforeDiscount,
    taxesAndCharges,
    totalDiscount,
    totalFoodDiscounts,
    totalSystemDiscounts,
    totalDeliveryDiscountByCoupon,
    tableNumber: order.tableNumber,
    deliveryFee: deliveryFees?.fee || 0,
    deliveryType,
    deliveryService,
    address: deliveryAddress,
    queueNumber,
    accountId: selectedUserAccount?._id,
    customerFullName: selectedUserAccount?.fullName,
    customerMobilePhone: selectedUserAccount?.mobilePhone,
    customerReferralCode: selectedUserAccount?.customerReferralCode,
    accountRole: !!selectedUserAccount ? "user" : "access",
    branchName: order.branchName,
    branchContactNumber: order.branchContactNumber,
    branchAddress: order.branchAddress
  }
}

const printCart = async (
  tempOrderId,
  order,
  deliveryType = "dineIn",
  deliveryService = "",
  deliveryAddress = "",
  discounts = [],
  deliveryFees = null,
  orderStatus = "pending",
  selectedUserAccount = null
) => {
  const printDetails =
    getPrintDetails(
      tempOrderId,
      order,
      deliveryType,
      deliveryService,
      deliveryAddress,
      discounts,
      deliveryFees,
      orderStatus,
      selectedUserAccount
    )

  const printers =
    store.getState().printers

  await Promise.all(
    printers.map(
      async (printerSettings, printerIndex) => {
        const thisPrinterOrderItems =
          printDetails.items.filter((item) =>
            item.categoryIds.some(cn =>
              printerSettings.categoryIds.includes(cn)
            )
          )

        const printPerItemEnabled =
          printerSettings.forOrderItems &&
          !printerSettings.perOrderItemQuanity

        const printPerItems =
          printPerItemEnabled ? thisPrinterOrderItems : []

        const printPerItemQuantityEnabled =
          printerSettings.forOrderItems &&
          printerSettings.perOrderItemQuanity

        const printPerItemQuantity = []

        if (printPerItemQuantityEnabled) {
          thisPrinterOrderItems.forEach(thisPrinterOrderItem => {
            for (let i = 0; i < thisPrinterOrderItem.quantity; i++) {
              printPerItemQuantity.push(thisPrinterOrderItem)
            }
          })
        }

        const noPrintPerItem =
          printPerItems.length === 0 &&
          printPerItemQuantity.length === 0

        const printKitchenReceipt =
          printerSettings.forKitchen

        const printCustomerReceipt =
          printerSettings.forCustomer

        const emptyPrint =
          noPrintPerItem &&
          !printCustomerReceipt &&
          !printKitchenReceipt

        if (emptyPrint) return

        const printData = {
          ...printDetails,
          printPerItems,
          printPerItemQuantity,
          printKitchenReceipt,
          printCustomerReceipt,
          thisPrinterOrderItems
        }

        const request = {
          printType: "order",
          printData,
          printerIndex,
          printerSettings,
          saveToFailedPrints: true
        }

        try {
          createPrintRequest(request)
        } catch (err) {
          console.log(err)
        }
      }
    )
  )
}

export default printCart